import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandItem, CommandEmpty } from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import {
  Pyramid, Database, Loader2, ArrowLeft, ArrowRight, FileText,
  LayoutDashboard, CheckCircle2, AlertTriangle, Search, ExternalLink, LayoutGrid, Filter,
  ChevronsUpDown, Wand2, Table2,
} from 'lucide-react';

// Step-by-step Pyramid Analytics import: connection → item(s) → model → review.
// Supports single-item preview or multi-select bulk import.
export function PyramidImportDialog({ open, onOpenChange, models = [] }) {
  const navigate = useNavigate();
  const [step, setStep] = useState('connection'); // connection | item | model | review
  const [busy, setBusy] = useState(false);

  const [connections, setConnections] = useState([]);
  const [connectionId, setConnectionId] = useState('');
  const [items, setItems] = useState([]);
  const [itemFilter, setItemFilter] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [parsed, setParsed] = useState(null); // single-item preview
  const [modelId, setModelId] = useState('');
  const [name, setName] = useState('');
  const [target, setTarget] = useState('report'); // 'report' | 'dashboard'
  const [result, setResult] = useState(null);       // single
  const [batchResult, setBatchResult] = useState(null); // bulk
  const [latestOnly, setLatestOnly] = useState(true);

  const reset = useCallback(() => {
    setStep('connection'); setBusy(false);
    setConnections([]); setConnectionId(''); setItems([]); setItemFilter('');
    setSelectedIds([]); setParsed(null); setModelId(''); setName(''); setTarget('report'); setResult(null); setBatchResult(null); setLatestOnly(true);
  }, []);

  useEffect(() => {
    if (!open) return;
    reset();
    setBusy(true);
    api.listPyramidConnections()
      .then((rows) => setConnections(rows || []))
      .catch((e) => toast.error(e.message || 'Could not load connections', { duration: Infinity, closeButton: true }))
      .finally(() => setBusy(false));
  }, [open, reset]);

  const loadItems = async (latest = latestOnly) => {
    if (!connectionId) { toast.error('Select a connection'); return; }
    setBusy(true);
    try {
      const res = await api.listPyramidItems({ source_id: connectionId, latest_only: latest });
      setItems(res.items || []);
      setStep('item');
      if (!(res.items || []).length) toast.message('No reports or dashboards found in this repository.');
    } catch (e) { toast.error(e.message || 'Could not read the repository', { duration: Infinity, description: 'Click to dismiss. Please copy this message so we can diagnose the Browse failure.', closeButton: true }); }
    finally { setBusy(false); }
  };

  const toggle = (id) => setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  const selectedItems = items.filter((it) => selectedIds.includes(it.id));

  const proceedFromItems = async () => {
    if (!selectedIds.length) { toast.error('Select at least one report'); return; }
    if (selectedIds.length === 1) {
      // single → parse for a rich preview
      const item = selectedItems[0];
      setBusy(true);
      try {
        const res = await api.parsePyramidImport({
          source_id: connectionId, item_id: item.id, item_type: item.item_type,
          sub_type: item.sub_type, name: item.name,
        });
        setParsed(res);
        setName(item.name || '');
        setStep('model');
      } catch (e) { toast.error(e.message || 'Could not read that item'); }
      finally { setBusy(false); }
    } else {
      setParsed(null);
      setStep('model');
    }
  };

  const summarize = (rev) => {
    const m = rev?.matched?.length || 0;
    const u = rev?.unmatched?.length || 0;
    return { matched: m, total: m + u };
  };

  const generate = async () => {
    if (!modelId) { toast.error('Select a semantic model'); return; }
    setBusy(true);
    try {
      if (parsed && selectedIds.length === 1) {
        const res = await api.generatePyramidImport({ session_id: parsed.session_id, model_id: modelId, name, target });
        setResult(res);
        const s = summarize(res.review);
        if (s.total > 0) toast.success(`${s.matched} of ${s.total} fields matched`);
      } else {
        const res = await api.generatePyramidBatch({
          source_id: connectionId, model_id: modelId, target,
          items: selectedItems.map((it) => ({ id: it.id, item_type: it.item_type, sub_type: it.sub_type, name: it.name })),
        });
        setBatchResult(res);
        const tot = (res.results || []).reduce((a, r) => { const s = summarize(r.review); return { m: a.m + s.matched, t: a.t + s.total }; }, { m: 0, t: 0 });
        if (tot.t > 0) toast.success(`${tot.m} of ${tot.t} fields matched across ${res.created} item(s)`);
      }
      setStep('review');
    } catch (e) { toast.error(e.message || 'Import failed'); }
    finally { setBusy(false); }
  };

  // Reflect an inline remap: move newly-mapped names from unmatched → matched.
  const applyRemapToResult = (mappedNames) => {
    setResult((prev) => {
      if (!prev) return prev;
      const set = new Set(mappedNames);
      const rev = prev.review || {};
      return {
        ...prev,
        review: {
          ...rev,
          matched: [...(rev.matched || []), ...mappedNames],
          unmatched: (rev.unmatched || []).filter((n) => !set.has(n)),
        },
      };
    });
  };

  const openReport = (rid, tgt) => { onOpenChange(false); navigate(`/dashboards/builder?id=${rid}${tgt === 'dashboard' ? '' : '&mode=report'}`); };

  const filteredItems = items.filter((it) =>
    !itemFilter || (it.name || '').toLowerCase().includes(itemFilter.toLowerCase()));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="pyramid-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pyramid className="w-5 h-5 text-amber-400" /> Import from Pyramid Analytics
          </DialogTitle>
        </DialogHeader>
        <DialogDescription className="text-sm text-muted-foreground -mt-1">
          Rebuild Pyramid reports or dashboards against one of your semantic models.
        </DialogDescription>

        {/* Step: choose connection */}
        {step === 'connection' && (
          <div className="space-y-4 mt-2" data-testid="pyramid-step-connection">
            <div>
              <Label className="text-sm">Pyramid content repository (PostgreSQL)</Label>
              {busy && !connections.length ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground mt-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading connections…</div>
              ) : connections.length ? (
                <Select value={connectionId} onValueChange={setConnectionId}>
                  <SelectTrigger className="mt-1" data-testid="pyramid-connection-select"><SelectValue placeholder="Select a connection…" /></SelectTrigger>
                  <SelectContent>
                    {connections.map((c) => (<SelectItem key={c.source_id} value={c.source_id}>{c.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="mt-2 p-3 rounded-lg bg-muted/20 text-xs text-muted-foreground flex items-start gap-2">
                  <Database className="w-4 h-4 mt-0.5 text-amber-400 shrink-0" />
                  <span>No PostgreSQL data source found. Add one in <strong>Data Sources</strong> pointing at your Pyramid content repository, then come back here.</span>
                </div>
              )}
            </div>
            <Button onClick={() => loadItems()} disabled={busy || !connectionId} className="w-full gap-2 bg-primary text-primary-foreground hover:opacity-90" data-testid="pyramid-load-items-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />} Browse reports
            </Button>
          </div>
        )}

        {/* Step: pick item(s) — multi-select */}
        {step === 'item' && (
          <div className="space-y-3 mt-2" data-testid="pyramid-step-item">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
                <Input value={itemFilter} onChange={(e) => setItemFilter(e.target.value)} placeholder="Search reports & dashboards…" className="pl-8" data-testid="pyramid-item-search" />
              </div>
              <button onClick={() => setSelectedIds(selectedIds.length === filteredItems.length ? [] : filteredItems.map((i) => i.id))}
                className="text-xs text-primary hover:underline whitespace-nowrap" data-testid="pyramid-select-all">
                {selectedIds.length === filteredItems.length && filteredItems.length ? 'Clear' : 'Select all'}
              </button>
            </div>
            <label className="flex items-center gap-2 text-[11px] text-muted-foreground cursor-pointer" data-testid="pyramid-item-latest-toggle">
              <Checkbox checked={latestOnly} onCheckedChange={(v) => { const nv = !!v; setLatestOnly(nv); loadItems(nv); }} />
              Latest versions only (hide older revisions)
            </label>
            <div className="max-h-72 overflow-auto space-y-1.5 pr-1">
              {filteredItems.map((it) => (
                <label key={it.id}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${selectedIds.includes(it.id) ? 'border-primary/50 bg-primary/5' : 'border-border/40 bg-muted/10 hover:border-primary/30'}`}
                  data-testid={`pyramid-item-${it.id}`}>
                  <Checkbox checked={selectedIds.includes(it.id)} onCheckedChange={() => toggle(it.id)} data-testid={`pyramid-item-check-${it.id}`} />
                  {it.item_type === 4 ? <LayoutDashboard className="w-5 h-5 text-indigo-400 shrink-0" /> : <FileText className="w-5 h-5 text-blue-400 shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{it.name}</div>
                    <div className="text-[10px] text-muted-foreground flex items-center gap-2">
                      <span>{it.type_label}</span>
                      {(it.version !== null && it.version !== undefined) && <Badge variant="outline" className="text-[8px] border-sky-500/40 bg-sky-500/10 text-sky-600 dark:text-sky-300" data-testid={`pyramid-item-version-${it.id}`}>v{it.version}</Badge>}
                      {it.modified_date && <span>· {String(it.modified_date).slice(0, 10)}</span>}
                    </div>
                  </div>
                  {!it.supported && <Badge variant="outline" className="text-[8px] border-amber-400/40 text-amber-400">review</Badge>}
                </label>
              ))}
              {!filteredItems.length && <div className="text-xs text-muted-foreground text-center py-8">No items match.</div>}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={() => setStep('connection')} className="gap-2 text-xs" data-testid="pyramid-back-connection"><ArrowLeft className="w-3.5 h-3.5" /> Back</Button>
              <Button onClick={proceedFromItems} disabled={busy || !selectedIds.length} className="flex-1 gap-2 bg-primary text-primary-foreground hover:opacity-90" data-testid="pyramid-items-next-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />} Continue{selectedIds.length ? ` (${selectedIds.length})` : ''}
              </Button>
            </div>
          </div>
        )}

        {/* Step: map onto a semantic model */}
        {step === 'model' && (
          <div className="space-y-4 mt-2" data-testid="pyramid-step-model">
            {parsed ? (
              <div className="p-3 bg-muted/20 rounded-lg text-xs space-y-1">
                <div className="font-medium text-foreground flex items-center gap-2">{parsed.title} <Badge variant="outline" className="text-[8px]">{parsed.type_label}</Badge></div>
                <div className="flex gap-3 text-muted-foreground">
                  <span>{parsed.summary?.body_elements || 0} visuals</span>
                  <span>{parsed.summary?.fields || 0} fields</span>
                  <span>{parsed.summary?.calculations || 0} calculations</span>
                </div>
              </div>
            ) : (
              <div className="p-3 bg-muted/20 rounded-lg text-xs">
                <span className="font-medium text-foreground">{selectedIds.length} items selected</span>
                <span className="text-muted-foreground"> — each becomes its own report.</span>
              </div>
            )}
            {parsed && (
              <div>
                <Label className="text-sm">Report name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1" data-testid="pyramid-name-input" />
              </div>
            )}
            <div>
              <Label className="text-sm">Map to semantic model</Label>
              <Select value={modelId} onValueChange={setModelId}>
                <SelectTrigger className="mt-1" data-testid="pyramid-model-select"><SelectValue placeholder="Select model…" /></SelectTrigger>
                <SelectContent>{models.map((m) => <SelectItem key={m.model_id} value={m.model_id}>{m.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">Import as</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <button type="button" onClick={() => setTarget('report')} data-testid="pyramid-target-report"
                  className={`flex items-start gap-2 p-3 rounded-xl border text-left transition-all ${target === 'report' ? 'border-primary/60 bg-primary/10' : 'border-border/40 bg-muted/10 hover:border-primary/30'}`}>
                  <FileText className={`w-4 h-4 mt-0.5 shrink-0 ${target === 'report' ? 'text-primary' : 'text-blue-400'}`} />
                  <div className="min-w-0">
                    <div className="text-sm font-medium">Report</div>
                    <div className="text-[10px] text-muted-foreground leading-tight">Paged, print-ready layout</div>
                  </div>
                </button>
                <button type="button" onClick={() => setTarget('dashboard')} data-testid="pyramid-target-dashboard"
                  className={`flex items-start gap-2 p-3 rounded-xl border text-left transition-all ${target === 'dashboard' ? 'border-primary/60 bg-primary/10' : 'border-border/40 bg-muted/10 hover:border-primary/30'}`}>
                  <LayoutDashboard className={`w-4 h-4 mt-0.5 shrink-0 ${target === 'dashboard' ? 'text-primary' : 'text-indigo-400'}`} />
                  <div className="min-w-0">
                    <div className="text-sm font-medium">Dashboard</div>
                    <div className="text-[10px] text-muted-foreground leading-tight">Interactive canvas of tiles</div>
                  </div>
                </button>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setStep('item')} className="gap-2 text-xs" data-testid="pyramid-back-item"><ArrowLeft className="w-3.5 h-3.5" /> Back</Button>
              <Button onClick={generate} disabled={busy || !modelId} className="flex-1 gap-2 bg-primary text-primary-foreground hover:opacity-90" data-testid="pyramid-generate-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />} {busy ? 'Importing…' : (parsed ? 'Import & review' : `Import ${selectedIds.length} & review`)}
              </Button>
            </div>
          </div>
        )}

        {/* Step: review */}
        {step === 'review' && result && (
          <div className="space-y-3 mt-2" data-testid="pyramid-step-review">
            <SuccessLine result={result} />
            <DataPreview previewQuery={result.preview_query} />
            <div className="max-h-64 overflow-auto space-y-3 pr-1"><ReviewBody review={result.review} layoutApprox={result.layout_approx} modelId={modelId} reportId={result.report_id} onRemapped={applyRemapToResult} /></div>
            <Button onClick={() => openReport(result.report_id, result.target)} className="w-full gap-2 bg-primary text-primary-foreground hover:opacity-90" data-testid="pyramid-open-builder-btn">
              <ExternalLink className="w-4 h-4" /> Open in builder
            </Button>
          </div>
        )}

        {step === 'review' && batchResult && (
          <div className="space-y-3 mt-2" data-testid="pyramid-step-review-batch">
            <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-xs flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" /> Imported {batchResult.created} of {batchResult.created + (batchResult.errors?.length || 0)} items.
            </div>
            <div className="max-h-72 overflow-auto space-y-2 pr-1">
              {batchResult.results.map((r) => (
                <div key={r.report_id} className="p-2.5 rounded-lg border border-border/40 bg-muted/10 space-y-1.5" data-testid={`pyramid-batch-item-${r.report_id}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium flex-1 truncate">{r.name}</span>
                    <button onClick={() => openReport(r.report_id, r.target)} className="text-[10px] text-primary hover:underline flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Open</button>
                  </div>
                  <div className="text-[10px] text-muted-foreground flex flex-wrap gap-2">
                    <span>{r.visuals_created} visual(s)</span>
                    {r.measures_created ? <span>{r.measures_created} measure(s)</span> : null}
                    {r.layout_approx ? <span className="text-indigo-400">layout kept</span> : null}
                    {r.review.global_slicers?.length ? <span className="text-sky-400">{r.review.global_slicers.length} slicer(s)</span> : null}
                    {r.review.unmatched?.length ? <span className="text-amber-400">{r.review.unmatched.length} to map</span> : null}
                    {r.review.formulas?.filter((f) => f.status === 'manual').length ? <span className="text-amber-400">{r.review.formulas.filter((f) => f.status === 'manual').length} formula(s) manual</span> : null}
                  </div>
                </div>
              ))}
              {(batchResult.errors || []).map((e, i) => (
                <div key={i} className="p-2 rounded bg-rose-500/10 text-[11px] flex items-start gap-2"><AlertTriangle className="w-3.5 h-3.5 text-rose-400 mt-0.5" /><span><strong>{e.name}</strong> — {e.error}</span></div>
              ))}
            </div>
            <Button onClick={() => onOpenChange(false)} className="w-full" variant="outline" data-testid="pyramid-batch-done-btn">Done</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SuccessLine({ result }) {
  return (
    <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-xs flex items-center gap-2">
      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
      <span>Created <strong>{result.name}</strong> — {result.visuals_created} visual(s){result.measures_created ? `, ${result.measures_created} calculation(s) translated` : ''}.</span>
    </div>
  );
}

// Live data preview for the first imported visual — confirms the import returns
// real rows before the author opens the builder. Fetches a small sample only
// (not a full count) for performance.
function DataPreview({ previewQuery }) {
  const PREVIEW_LIMIT = 20; // small sample ceiling — never a full table scan
  const [state, setState] = useState({ loading: true, rows: null, error: '' });
  const q = previewQuery || {};
  const dims = q.dimensions || [];
  const measures = q.measures || [];
  const columns = [...dims, ...measures];

  useEffect(() => {
    let cancelled = false;
    if (!q.model_id || columns.length === 0) {
      setState({ loading: false, rows: [], error: columns.length === 0 ? 'no-fields' : '' });
      return;
    }
    setState({ loading: true, rows: null, error: '' });
    api.executeQuery({ model_id: q.model_id, dimensions: dims, measures, filters: [], limit: PREVIEW_LIMIT })
      .then((res) => {
        if (cancelled) return;
        const rows = res?.data || res?.rows || [];
        setState({ loading: false, rows, error: '' });
      })
      .catch((err) => { if (!cancelled) setState({ loading: false, rows: [], error: err.message || 'Preview failed' }); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.model_id, JSON.stringify(columns)]);

  const fmt = (v) => {
    if (v === null || v === undefined || v === '') return '—';
    if (typeof v === 'number') return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
    return String(v);
  };

  const rows = state.rows || [];
  const hasMore = rows.length >= PREVIEW_LIMIT;
  const countLabel = hasMore ? `${PREVIEW_LIMIT}+ sample rows` : `${rows.length} ${rows.length === 1 ? 'row' : 'rows'}`;

  return (
    <div className="rounded-lg border border-border/40 bg-muted/10 overflow-hidden" data-testid="pyramid-data-preview">
      <div className="px-3 py-1.5 flex items-center gap-1.5 border-b border-border/30 bg-muted/20">
        <Table2 className="w-3.5 h-3.5 text-primary" />
        <span className="text-[11px] font-semibold text-foreground">Data preview</span>
        {q.title ? <span className="text-[10px] text-muted-foreground truncate">· {q.title}</span> : null}
        {!state.loading && !state.error && rows.length > 0 && (
          <Badge variant="outline" className="ml-auto text-[9px] px-1.5 py-0 h-4 border-border/50 text-muted-foreground" data-testid="pyramid-preview-rowcount">
            {countLabel}
          </Badge>
        )}
      </div>
      {state.loading ? (
        <div className="flex items-center gap-2 px-3 py-4 text-[11px] text-muted-foreground" data-testid="pyramid-preview-loading">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Running the imported query…
        </div>
      ) : state.error === 'no-fields' ? (
        <div className="px-3 py-3 text-[11px] text-amber-400 flex items-start gap-2" data-testid="pyramid-preview-empty">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5" />
          No fields were bound to the first visual — map the unmatched fields below, then re-check.
        </div>
      ) : state.error ? (
        <div className="px-3 py-3 text-[11px] text-rose-400 flex items-start gap-2" data-testid="pyramid-preview-error">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5" /> {state.error}
        </div>
      ) : (rows.length > 0) ? (
        <>
          <div className="max-h-40 overflow-auto">
            <table className="w-full text-[10px]" data-testid="pyramid-preview-table">
              <thead className="sticky top-0 bg-muted/40">
                <tr>{columns.map((c) => (
                  <th key={c} className="text-left font-semibold px-2 py-1 text-muted-foreground whitespace-nowrap">{c}</th>
                ))}</tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i} className="border-t border-border/20">
                    {columns.map((c) => (
                      <td key={c} className="px-2 py-1 whitespace-nowrap text-foreground/90">{fmt(row[c])}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-3 py-1 text-[9px] text-muted-foreground/70 border-t border-border/20">
            {hasMore ? `Showing a ${PREVIEW_LIMIT}-row sample for performance — the full report loads all rows.` : 'Sample preview.'}
          </div>
        </>
      ) : (
        <div className="px-3 py-3 text-[11px] text-amber-400 flex items-start gap-2" data-testid="pyramid-preview-norows">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5" />
          The query ran but returned no rows. Check the field mappings and data source binding.
        </div>
      )}
    </div>
  );
}

function ReviewBody({ review, layoutApprox, modelId, reportId, onRemapped }) {
  if (!review) return null;
  return (
    <>
      {layoutApprox && (
        <div className="text-[11px] text-indigo-300 flex items-center gap-1.5"><LayoutGrid className="w-3.5 h-3.5" /> Original tile layout preserved (approximate).</div>
      )}
      {review.global_slicers?.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1"><Filter className="w-3 h-3" /> Global slicers</div>
          <div className="flex flex-wrap gap-1.5">{review.global_slicers.map((x, i) => <Badge key={i} variant="outline" className="text-[10px] border-sky-400/40 text-sky-300">{x}</Badge>)}</div>
        </div>
      )}
      {review.matched?.length > 0 && <ReviewGroup title="Mapped fields & measures" tone="ok" items={review.matched} />}
      {review.unmatched?.length > 0 && (
        modelId && reportId
          ? <InlineMapper unmatched={review.unmatched} suggestions={review.suggestions || {}} modelId={modelId} reportId={reportId} onApplied={onRemapped} />
          : <ReviewGroup title="Needs manual mapping" tone="warn" items={review.unmatched} />
      )}
      {review.formulas?.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Calculations</div>
          <div className="space-y-1">
            {review.formulas.map((f, i) => (
              <div key={i} className="text-xs px-2 py-1.5 rounded bg-muted/10 flex items-start gap-2" data-testid={`pyramid-formula-${f.status}`}>
                <Badge variant="outline" className={`text-[8px] shrink-0 font-medium ${f.status === 'manual' ? 'border-amber-500/40 bg-amber-500/15 text-amber-700 dark:text-amber-300' : 'border-emerald-500/40 bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'}`}>{f.status}</Badge>
                <div className="min-w-0">
                  <div className="font-medium truncate">{f.name}</div>
                  <div className="text-[10px] text-muted-foreground">{f.note}</div>
                  {f.pql && <code className="text-[10px] text-muted-foreground/80 break-all">{f.pql}</code>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {review.items?.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Items needing attention</div>
          <div className="space-y-1">
            {review.items.map((it, i) => (
              <div key={i} className="text-xs px-2 py-1.5 rounded bg-amber-500/10 flex items-start gap-2">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />
                <div><span className="font-medium">{it.name}</span><span className="text-muted-foreground"> — {it.reason}</span></div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

// Inline field mapper: pick a real model field/measure for each unmatched name,
// then apply all at once so the imported report needs no fix-up in the builder.
function InlineMapper({ unmatched, suggestions = {}, modelId, reportId, onApplied }) {
  const [choices, setChoices] = useState(() => {
    const init = {};
    Object.entries(suggestions).forEach(([k, v]) => { if (v && v.name) init[k] = { ...v, suggested: true }; });
    return init;
  });
  const [applying, setApplying] = useState(false);
  const [done, setDone] = useState([]); // names already applied

  const pending = unmatched.filter((n) => !done.includes(n));
  const readyCount = Object.keys(choices).filter((n) => choices[n] && !done.includes(n)).length;
  const suggestedCount = pending.filter((n) => choices[n]?.suggested).length;

  const apply = async () => {
    const mappings = {};
    Object.entries(choices).forEach(([oldName, f]) => { if (f && !done.includes(oldName)) mappings[oldName] = f.name; });
    if (!Object.keys(mappings).length) { toast.error('Pick a field for at least one item'); return; }
    setApplying(true);
    try {
      const res = await api.remapPyramidImport({ report_id: reportId, mappings });
      const names = Object.keys(mappings);
      setDone((d) => [...d, ...names]);
      onApplied?.(names);
      toast.success(`Mapped ${names.length} field${names.length > 1 ? 's' : ''} (${res.remapped} reference${res.remapped === 1 ? '' : 's'} updated)`);
    } catch (e) { toast.error(e.message || 'Could not apply mapping'); }
    finally { setApplying(false); }
  };

  if (!pending.length) return null;
  return (
    <div data-testid="pyramid-inline-mapper">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5 flex items-center gap-1">
        <AlertTriangle className="w-3 h-3 text-amber-500" /> Needs manual mapping
        {suggestedCount > 0 && <span className="ml-1 text-primary normal-case tracking-normal">· {suggestedCount} suggested — review &amp; apply</span>}
      </div>
      <div className="space-y-1.5">
        {pending.map((n) => (
          <div key={n} className="flex items-center gap-2" data-testid={`map-row-${n}`}>
            <Badge variant="outline" className="text-[10px] font-medium border-amber-500/40 bg-amber-500/15 text-amber-700 dark:text-amber-300 shrink-0 max-w-[38%] truncate" title={n}>{n}</Badge>
            <ArrowRight className="w-3 h-3 text-muted-foreground shrink-0" />
            <FieldPicker modelId={modelId} value={choices[n]} suggested={choices[n]?.suggested} onChange={(f) => setChoices((c) => ({ ...c, [n]: f ? { ...f, suggested: false } : f }))} testid={`field-picker-${n}`} />
          </div>
        ))}
      </div>
      <Button size="sm" onClick={apply} disabled={applying || readyCount === 0}
        className="mt-2 h-7 gap-1.5 text-xs bg-primary text-primary-foreground hover:opacity-90" data-testid="pyramid-apply-mapping-btn">
        {applying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
        Apply mapping{readyCount ? ` (${readyCount})` : ''}
      </Button>
    </div>
  );
}

// Searchable model field/measure picker (server-side search over large models).
function FieldPicker({ modelId, value, onChange, testid, suggested }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await api.searchPyramidModelFields(modelId, q);
        if (alive) setResults(res.fields || []);
      } catch { if (alive) setResults([]); }
      finally { if (alive) setLoading(false); }
    }, 220);
    return () => { alive = false; clearTimeout(t); };
  }, [open, q, modelId]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testid}
          className={`flex-1 min-w-0 flex items-center gap-1 px-2 py-1 rounded-lg border text-xs transition-colors ${suggested ? 'border-primary/50 bg-primary/10' : 'border-border/50 bg-muted/30 hover:border-primary/40'}`}>
          <span className={`truncate ${value ? 'text-foreground' : 'text-muted-foreground'}`}>{value ? value.label : 'Choose field…'}</span>
          {suggested && <Wand2 className="w-3 h-3 shrink-0 text-primary" title="Suggested — click to change" />}
          <ChevronsUpDown className="w-3 h-3 ml-auto shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-64" align="start">
        <Command shouldFilter={false}>
          <CommandInput value={q} onValueChange={setQ} placeholder="Search fields & measures…" className="text-xs" />
          <CommandList>
            {loading && <div className="px-3 py-2 text-[11px] text-muted-foreground flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Searching…</div>}
            {!loading && <CommandEmpty className="px-3 py-2 text-[11px]">No matching field.</CommandEmpty>}
            {results.map((r) => (
              <CommandItem key={`${r.kind}:${r.name}`} value={r.name} onSelect={() => { onChange(r); setOpen(false); }} className="text-xs">
                <span className="truncate">{r.label}</span>
                <Badge variant="outline" className={`ml-auto text-[8px] shrink-0 ${r.kind === 'measure' ? 'border-sky-400/40 text-sky-500' : 'border-emerald-400/40 text-emerald-600 dark:text-emerald-400'}`}>{r.kind}</Badge>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function ReviewGroup({ title, tone, items }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{title}</div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((x, i) => (
          <Badge key={i} variant="outline" className={`text-[10px] font-medium ${tone === 'warn' ? 'border-amber-500/40 bg-amber-500/15 text-amber-700 dark:text-amber-300' : 'border-emerald-500/40 bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'}`}>{x}</Badge>
        ))}
      </div>
    </div>
  );
}
